import {
  type DragEvent,
  type KeyboardEvent,
  type ReactElement,
  useCallback,
  useState,
} from 'react';

import { cn } from '../lib/utils/cn';

interface DropZoneProps {
  readonly onFile: (file: File) => void;
  /** Fired when the user clicks the zone (used to trigger a picker). */
  readonly onActivate: () => void;
  readonly children?: ReactElement | string;
  readonly ariaLabel?: string;
}

/**
 * Keyboard-accessible drop-zone. Fires `onFile(file)` for the first
 * dropped file and `onActivate()` on click / Enter / Space (the
 * picker path). Non-PDF filtering happens in the caller (the hook
 * layer) — this component is provider-agnostic.
 */
export function DropZone({
  onFile,
  onActivate,
  children,
  ariaLabel = 'Drop a PDF here or activate to browse',
}: DropZoneProps): ReactElement {
  const [isOver, setIsOver] = useState(false);

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsOver(true);
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsOver(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      setIsOver(false);
      const file = event.dataTransfer.files[0];
      if (file) {
        onFile(file);
      }
    },
    [onFile],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        // Stop the event bubbling to a parent menu / dialog / list
        // that might handle Enter/Space (activate row, close modal).
        event.stopPropagation();
        onActivate();
      }
    },
    [onActivate],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      // Semantic hooks for tests + downstream styling — decoupled
      // from Tailwind class names AND from the aria-label prose so a
      // design refresh doesn't cascade to the test suite. The
      // `data-testid` is used by the Playwright zero-network E2E in
      // `apps/web/e2e/guest-flow.spec.ts` to locate the drop-zone
      // without depending on aria-label content.
      data-testid="dropzone"
      data-state={isOver ? 'over' : 'idle'}
      className={cn(
        'flex min-h-[220px] w-full max-w-xl cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome-accent focus-visible:ring-offset-2',
        isOver
          ? 'border-chrome-accent bg-chrome-accent/5 text-chrome-text-primary'
          : 'border-chrome-border bg-chrome-surface text-chrome-text-secondary hover:border-chrome-accent',
      )}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      onDragEnter={onDragOver}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
    </div>
  );
}
